import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchSimuladoresConteudoAluno,
  fetchSimuladoresConteudoState,
  saveSimuladoresConteudoErrors,
  saveSimuladoresConteudoProgress,
  saveSimuladoresConteudoStructure,
  createSimuladorCategoria,
  updateSimuladorCategoria as updateSimuladorCategoriaApi,
  deleteSimuladorCategoria,
  restoreSimuladorCategoria as restoreSimuladorCategoriaApi,
  createSimuladorCurso,
  updateSimuladorCurso as updateSimuladorCursoApi,
  deleteSimuladorCurso,
  restoreSimuladorCurso as restoreSimuladorCursoApi,
  createSimuladorModulo,
  updateSimuladorModulo as updateSimuladorModuloApi,
  deleteSimuladorModulo,
  restoreSimuladorModulo as restoreSimuladorModuloApi,
  createSimuladorAula,
  updateSimuladorAula as updateSimuladorAulaApi,
  deleteSimuladorAula,
  restoreSimuladorAula as restoreSimuladorAulaApi,
} from "../services/simuladoresConteudoApi";
import { normalizeConteudosData } from "../data";

const serialize = (value) => JSON.stringify(value ?? null);

const EMPTY_STATE = normalizeConteudosData({
  categorias: [],
  cursos: [],
  modulos: [],
  aulas: [],
  erros: [],
  progresso: {},
});

/**
 * Hook que gerencia o estado de Simuladores-Conteudo.
 * Espelha o comportamento do conteudos no useAppShellState, mas independente.
 */
export function useSimuladoresConteudoState({
  adminLogged,
  currentStudent,
  view,
  adminAba,
}) {
  const [simuladoresConteudo, setSimuladoresConteudoRaw] = useState(EMPTY_STATE);
  const [simuladoresConteudoNavigationTarget, setSimuladoresConteudoNavigationTarget] = useState(null);

  const loadedRef = useRef(false);
  const apiInitializedRef = useRef(false);
  const bootstrapRef = useRef(false);
  const skipSyncRef = useRef(true);
  const syncTimerRef = useRef(null);
  const enterRefreshRef = useRef("");
  const previousRef = useRef(EMPTY_STATE);

  const setSimuladoresConteudo = useCallback((updater) => {
    setSimuladoresConteudoRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return normalizeConteudosData(next || EMPTY_STATE);
    });
  }, []);

  const addSimuladorCategoria = useCallback(async (payload) => {
    const categoria = await createSimuladorCategoria(payload);
    if (!categoria) throw new Error("Falha ao criar categoria");

    setSimuladoresConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        categorias: [...(prev.categorias || []), categoria],
      });
      previousRef.current = nextValue;
      return nextValue;
    });

    return categoria;
  }, []);

  const updateSimuladorCategoria = useCallback(async (categoriaId, payload) => {
    const categoria = await updateSimuladorCategoriaApi(categoriaId, payload);
    if (!categoria) throw new Error("Falha ao atualizar categoria");

    setSimuladoresConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        categorias: (prev.categorias || []).map((item) =>
          item.id === categoriaId ? categoria : item,
        ),
      });
      previousRef.current = nextValue;
      return nextValue;
    });

    return categoria;
  }, []);

  const removeSimuladorCategoria = useCallback(async (categoriaId) => {
    await deleteSimuladorCategoria(categoriaId);

    setSimuladoresConteudoRaw((prev) => {
      const cursosIds = (prev.cursos || [])
        .filter((item) => item.cat_id === categoriaId)
        .map((item) => item.id);
      const modulosIds = (prev.modulos || [])
        .filter((item) => cursosIds.includes(item.curso_id))
        .map((item) => item.id);
      const aulasIds = (prev.aulas || [])
        .filter((item) => modulosIds.includes(item.modulo_id))
        .map((item) => item.id);

      const nextValue = normalizeConteudosData({
        ...prev,
        categorias: (prev.categorias || []).filter(
          (item) => item.id !== categoriaId,
        ),
        cursos: (prev.cursos || []).filter(
          (item) => item.cat_id !== categoriaId,
        ),
        modulos: (prev.modulos || []).filter(
          (item) => !cursosIds.includes(item.curso_id),
        ),
        aulas: (prev.aulas || []).filter(
          (item) => !modulosIds.includes(item.modulo_id),
        ),
        progresso: Object.keys(prev.progresso || {}).reduce(
          (acc, studentKey) => {
            const currentProgress = { ...(prev.progresso?.[studentKey] || {}) };
            aulasIds.forEach((aulaId) => delete currentProgress[aulaId]);
            acc[studentKey] = currentProgress;
            return acc;
          },
          {},
        ),
      });

      previousRef.current = nextValue;
      return nextValue;
    });
  }, []);

  const restoreSimuladorCategoria = useCallback(async (categoriaId) => {
    await restoreSimuladorCategoriaApi(categoriaId);

    const { snapshot } = await fetchSimuladoresConteudoState();
    const normalized = normalizeConteudosData(snapshot);
    skipSyncRef.current = true;
    previousRef.current = normalized;
    setSimuladoresConteudoRaw(normalized);
  }, []);

  const addSimuladorCurso = useCallback(async (payload) => {
    const curso = await createSimuladorCurso(payload);
    if (!curso) throw new Error("Falha ao criar curso");

    setSimuladoresConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        cursos: [...(prev.cursos || []), curso],
      });
      previousRef.current = nextValue;
      return nextValue;
    });

    return curso;
  }, []);

  const updateSimuladorCurso = useCallback(async (cursoId, payload) => {
    const curso = await updateSimuladorCursoApi(cursoId, payload);
    if (!curso) throw new Error("Falha ao atualizar curso");

    setSimuladoresConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        cursos: (prev.cursos || []).map((item) =>
          item.id === cursoId ? curso : item,
        ),
      });
      previousRef.current = nextValue;
      return nextValue;
    });

    return curso;
  }, []);

  const removeSimuladorCurso = useCallback(async (cursoId) => {
    await deleteSimuladorCurso(cursoId);

    setSimuladoresConteudoRaw((prev) => {
      const modulosIds = (prev.modulos || [])
        .filter((item) => item.curso_id === cursoId)
        .map((item) => item.id);
      const aulasIds = (prev.aulas || [])
        .filter((item) => modulosIds.includes(item.modulo_id))
        .map((item) => item.id);

      const nextValue = normalizeConteudosData({
        ...prev,
        cursos: (prev.cursos || []).filter((item) => item.id !== cursoId),
        modulos: (prev.modulos || []).filter(
          (item) => item.curso_id !== cursoId,
        ),
        aulas: (prev.aulas || []).filter(
          (item) => !modulosIds.includes(item.modulo_id),
        ),
        progresso: Object.keys(prev.progresso || {}).reduce(
          (acc, studentKey) => {
            const currentProgress = { ...(prev.progresso?.[studentKey] || {}) };
            aulasIds.forEach((aulaId) => delete currentProgress[aulaId]);
            acc[studentKey] = currentProgress;
            return acc;
          },
          {},
        ),
      });

      previousRef.current = nextValue;
      return nextValue;
    });
  }, []);

  const restoreSimuladorCurso = useCallback(async (cursoId) => {
    await restoreSimuladorCursoApi(cursoId);

    const { snapshot } = await fetchSimuladoresConteudoState();
    const normalized = normalizeConteudosData(snapshot);
    skipSyncRef.current = true;
    previousRef.current = normalized;
    setSimuladoresConteudoRaw(normalized);
  }, []);

  const addSimuladorModulo = useCallback(async (payload) => {
    const modulo = await createSimuladorModulo(payload);
    if (!modulo) throw new Error("Falha ao criar modulo");

    setSimuladoresConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        modulos: [...(prev.modulos || []), modulo],
      });
      previousRef.current = nextValue;
      return nextValue;
    });

    return modulo;
  }, []);

  const updateSimuladorModulo = useCallback(async (moduloId, payload) => {
    const modulo = await updateSimuladorModuloApi(moduloId, payload);
    if (!modulo) throw new Error("Falha ao atualizar modulo");

    setSimuladoresConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        modulos: (prev.modulos || []).map((item) =>
          item.id === moduloId ? modulo : item,
        ),
      });
      previousRef.current = nextValue;
      return nextValue;
    });

    return modulo;
  }, []);

  const removeSimuladorModulo = useCallback(async (moduloId) => {
    await deleteSimuladorModulo(moduloId);

    setSimuladoresConteudoRaw((prev) => {
      const aulasIds = (prev.aulas || [])
        .filter((item) => item.modulo_id === moduloId)
        .map((item) => item.id);

      const nextValue = normalizeConteudosData({
        ...prev,
        modulos: (prev.modulos || []).filter((item) => item.id !== moduloId),
        aulas: (prev.aulas || []).filter(
          (item) => item.modulo_id !== moduloId,
        ),
        progresso: Object.keys(prev.progresso || {}).reduce(
          (acc, studentKey) => {
            const currentProgress = { ...(prev.progresso?.[studentKey] || {}) };
            aulasIds.forEach((aulaId) => delete currentProgress[aulaId]);
            acc[studentKey] = currentProgress;
            return acc;
          },
          {},
        ),
      });

      previousRef.current = nextValue;
      return nextValue;
    });
  }, []);

  const restoreSimuladorModulo = useCallback(async (moduloId, payload = {}) => {
    await restoreSimuladorModuloApi(moduloId, payload);

    const { snapshot } = await fetchSimuladoresConteudoState();
    const normalized = normalizeConteudosData(snapshot);
    skipSyncRef.current = true;
    previousRef.current = normalized;
    setSimuladoresConteudoRaw(normalized);
  }, []);

  const addSimuladorAula = useCallback(async (payload) => {
    const aula = await createSimuladorAula(payload);
    if (!aula) throw new Error("Falha ao criar aula");

    setSimuladoresConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        aulas: [...(prev.aulas || []), aula],
      });
      previousRef.current = nextValue;
      return nextValue;
    });

    return aula;
  }, []);

  const updateSimuladorAula = useCallback(async (aulaId, payload) => {
    const aula = await updateSimuladorAulaApi(aulaId, payload);
    if (!aula) throw new Error("Falha ao atualizar aula");

    setSimuladoresConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        aulas: (prev.aulas || []).map((item) =>
          item.id === aulaId ? aula : item,
        ),
      });
      previousRef.current = nextValue;
      return nextValue;
    });

    return aula;
  }, []);

  const removeSimuladorAula = useCallback(async (aulaId) => {
    await deleteSimuladorAula(aulaId);

    setSimuladoresConteudoRaw((prev) => {
      const nextValue = normalizeConteudosData({
        ...prev,
        aulas: (prev.aulas || []).filter((item) => item.id !== aulaId),
        progresso: Object.keys(prev.progresso || {}).reduce(
          (acc, studentKey) => {
            const currentProgress = { ...(prev.progresso?.[studentKey] || {}) };
            delete currentProgress[aulaId];
            acc[studentKey] = currentProgress;
            return acc;
          },
          {},
        ),
      });
      previousRef.current = nextValue;
      return nextValue;
    });
  }, []);

  const restoreSimuladorAula = useCallback(async (aulaId, payload = {}) => {
    await restoreSimuladorAulaApi(aulaId, payload);

    const { snapshot } = await fetchSimuladoresConteudoState();
    const normalized = normalizeConteudosData(snapshot);
    skipSyncRef.current = true;
    previousRef.current = normalized;
    setSimuladoresConteudoRaw(normalized);
  }, []);

  const sessionKey = adminLogged
    ? "admin"
    : currentStudent
    ? String(currentStudent.id)
    : null;

  // Bootstrap do admin: envia estrutura se o servidor ainda não tem dados
  useEffect(() => {
    if (!adminLogged || !loadedRef.current) return;
    if (apiInitializedRef.current || bootstrapRef.current) return;

    const hasLocal =
      simuladoresConteudo?.categorias?.length ||
      simuladoresConteudo?.cursos?.length ||
      simuladoresConteudo?.modulos?.length ||
      simuladoresConteudo?.aulas?.length;

    if (!hasLocal) return;

    bootstrapRef.current = true;

    Promise.all([
      saveSimuladoresConteudoStructure(simuladoresConteudo),
      saveSimuladoresConteudoErrors(simuladoresConteudo?.erros || []),
    ])
      .then(() => {
        apiInitializedRef.current = true;
        previousRef.current = simuladoresConteudo;
        bootstrapRef.current = false;
      })
      .catch(() => {
        bootstrapRef.current = false;
      });
  }, [adminLogged, simuladoresConteudo]);

  // Carregamento/refresh ao entrar na tela
  useEffect(() => {
    const emSimuladoresAluno = view === "simuladores-conteudo";
    const emSimuladoresAdmin = view === "admin" && adminAba === "simuladores-conteudo-adm";

    if (!emSimuladoresAluno && !emSimuladoresAdmin) return;
    if (!sessionKey) return;

    const chave = `${sessionKey}:${view}:${adminAba}`;
    if (enterRefreshRef.current === chave) return;
    enterRefreshRef.current = chave;

    let active = true;

    if (emSimuladoresAdmin) {
      // Admin carrega sempre estado completo do servidor
      fetchSimuladoresConteudoState()
        .then(({ initialized, snapshot }) => {
          if (!active) return;
          apiInitializedRef.current = initialized;
          loadedRef.current = true;
          skipSyncRef.current = true;
          setSimuladoresConteudoRaw(snapshot || EMPTY_STATE);
          previousRef.current = snapshot || EMPTY_STATE;
        })
        .catch(() => {
          loadedRef.current = true;
        });
    } else if (emSimuladoresAluno && currentStudent) {
      // Aluno carrega snapshot leve
      if (!loadedRef.current) {
        fetchSimuladoresConteudoAluno()
          .then(({ initialized, snapshot }) => {
            if (!active) return;
            apiInitializedRef.current = initialized;
            loadedRef.current = true;
            skipSyncRef.current = true;
            setSimuladoresConteudoRaw(snapshot || EMPTY_STATE);
            previousRef.current = snapshot || EMPTY_STATE;
          })
          .catch(() => {
            loadedRef.current = true;
          });
      } else if (loadedRef.current) {
        // Refresh ao reentrar na tela
        fetchSimuladoresConteudoAluno()
          .then(({ snapshot }) => {
            if (!active) return;
            setSimuladoresConteudoRaw(snapshot || EMPTY_STATE);
            previousRef.current = snapshot || EMPTY_STATE;
          })
          .catch(() => {});
      }
    }

    return () => {
      active = false;
    };
  }, [adminAba, currentStudent, sessionKey, view]);

  // Sync automático ao mudar estado
  useEffect(() => {
    if (!loadedRef.current) return undefined;

    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      previousRef.current = simuladoresConteudo;
      return undefined;
    }

    const previous = previousRef.current;
    const studentKey =
      currentStudent?.id !== undefined && currentStudent?.id !== null
        ? String(currentStudent.id)
        : null;

    const syncFn = async () => {
      try {
        if (adminLogged) {
          const tasks = [];

          const prevStructure = {
            categorias: previous?.categorias || [],
            cursos: previous?.cursos || [],
            modulos: previous?.modulos || [],
            aulas: previous?.aulas || [],
          };
          const nextStructure = {
            categorias: simuladoresConteudo?.categorias || [],
            cursos: simuladoresConteudo?.cursos || [],
            modulos: simuladoresConteudo?.modulos || [],
            aulas: simuladoresConteudo?.aulas || [],
          };

          if (serialize(prevStructure) !== serialize(nextStructure)) {
            tasks.push(saveSimuladoresConteudoStructure(simuladoresConteudo));
          }

          if (
            serialize(previous?.erros || []) !==
            serialize(simuladoresConteudo?.erros || [])
          ) {
            tasks.push(saveSimuladoresConteudoErrors(simuladoresConteudo?.erros || []));
          }

          if (tasks.length) {
            await Promise.all(tasks);
            apiInitializedRef.current = true;
          }
        } else if (studentKey && apiInitializedRef.current) {
          const prevProgress = previous?.progresso?.[studentKey] || {};
          const nextProgress = simuladoresConteudo?.progresso?.[studentKey] || {};

          if (serialize(prevProgress) !== serialize(nextProgress)) {
            await saveSimuladoresConteudoProgress(nextProgress);
          }
        }

        previousRef.current = simuladoresConteudo;
      } catch {
        // Mantém cache local como fallback
      }
    };

    window.clearTimeout(syncTimerRef.current);
    syncTimerRef.current = window.setTimeout(syncFn, 500);

    return () => {
      window.clearTimeout(syncTimerRef.current);
    };
  }, [adminLogged, currentStudent?.id, simuladoresConteudo]);

  // Reset ao trocar de sessão
  useEffect(() => {
    loadedRef.current = false;
    apiInitializedRef.current = false;
    bootstrapRef.current = false;
    skipSyncRef.current = true;
    enterRefreshRef.current = "";
    setSimuladoresConteudoRaw(EMPTY_STATE);
    previousRef.current = EMPTY_STATE;
  }, [sessionKey]);

  return {
    simuladoresConteudo,
    setSimuladoresConteudo,
    simuladoresConteudoNavigationTarget,
    setSimuladoresConteudoNavigationTarget,
    addSimuladorCategoria,
    updateSimuladorCategoria,
    removeSimuladorCategoria,
    restoreSimuladorCategoria,
    addSimuladorCurso,
    updateSimuladorCurso,
    removeSimuladorCurso,
    restoreSimuladorCurso,
    addSimuladorModulo,
    updateSimuladorModulo,
    removeSimuladorModulo,
    restoreSimuladorModulo,
    addSimuladorAula,
    updateSimuladorAula,
    removeSimuladorAula,
    restoreSimuladorAula,
  };
}
